import React, { useCallback, useEffect, memo, useState, useMemo, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { css } from '@compiled/react';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Flex from '@atlaskit/primitives/components/flex';
import { xcss } from '@atlaskit/primitives/xcss';
import Box from '@atlaskit/primitives/box';
import { token } from '@atlaskit/tokens';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-atlaskit-theme
import { layers } from '@atlaskit/theme/constants';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTarget from '@atlaskit/onboarding/spotlight-target';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightTransition from '@atlaskit/onboarding/spotlight-transition';
// eslint-disable-next-line @atlaskit/design-system/use-spotlight-package
import SpotlightManager from '@atlaskit/onboarding/spotlight-manager';
import IconButton from '@atlaskit/button/icon/button';
import type { EditorAnalyticsAPI } from '@atlaskit/editor-common/analytics';
import { blockControlsSuppressionProps } from '@atlaskit/editor-common/block-controls-suppression';
import type {
	OptionalPlugin,
	PublicPluginAPI,
} from '@atlaskit/editor-common/types/next-editor-plugin';
import type { UserPreferencesPlugin } from '@atlaskit/editor-plugin-user-preferences/user-preferences-plugin-type';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronUpIcon from '@atlaskit/icon/core/chevron-up';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import { PopoverTarget } from '@atlaskit/spotlight/popover-target';
import { PopoverProvider } from '@atlaskit/spotlight/popover-provider';

import type { ObjectSidebarEditorAIAction } from '@atlassian/editor-plugin-ai/ConfluencePrebuilt';
import type { AIPlugin } from '@atlassian/editor-plugin-ai/aiPluginType';
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';
import { useReactProps_DO_NOT_USE } from '@atlassian/embedded-confluence/useReactProps';
import type { IframePassThroughProps } from '@atlassian/embedded-confluence/IframeElementType';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api';
import { ConversationAssistantUIModes } from '@atlassian/conversation-assistant';
import type { ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { useAudioFeatureFlags } from '@confluence/audio/entry-points/useAudioFeatureFlags.ts';
import { useRouteName } from '@confluence/route-manager/entry-points/RouteState.ts';
import { PDF_EXPORT_PAGE } from '@confluence/named-routes/entry-points/routes.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { AISmartButtonWrapper } from '@confluence/ai-rovo-button/entry-points/AISmartButtonWrapper.ts';
import { AIFloatingContextMenu } from '@confluence/ai-floating-context-menu/entry-points/AIFloatingContextMenu.ts';
import { useIsCurrentPageLive } from '@confluence/live-pages-utils/entry-points/useIsCurrentPageLive.ts';
import { fg } from '@confluence/feature-gating';
import type { PageHeaderLoomButton_contentContributors$key } from '@confluence/page-header-loom-button/entry-points/PageHeaderLoomContributors.ts';
import { PageHeaderLoomEntryPoint } from '@confluence/page-header-loom-button/entry-points/PageHeaderLoomEntryPoint.ts';
import { useCollabNetworkStatus } from '@confluence/collab-network-status';
import {
	expValEquals,
	isExperimentEnabled,
	UNSAFE_expValNoExposure,
} from '@confluence/feature-experiments';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import {
	useSidebarControlState,
	useSidebarControlExpandCollapsedState,
	useIsObjectSidebarShown,
	useObjectSidebarActions,
} from '@confluence/object-sidebar-api/entry-points/useObjectSidebar.ts';
import { useIsLoomDisabled } from '@confluence/loom-utils/entry-points/useIsLoomDisabled.ts';
import { LoadableAfterPaint, LoadableLazy } from '@confluence/loadable';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { useAIRovoButtonEnablement } from '@confluence/ai-common/entry-points/useAIRovoButtonEnablement.ts';
import { useIsMobileApp } from '@confluence/mobile-app-detection/entry-points/MobileAppDetection.ts';
import { isRemixOptedOut } from '@confluence/remix-availability/entry-points/isRemixOptedOut.ts';
import {
	ObjectSidebarRemixLifecycleOwnerProvider,
	useObjectSidebarRemixLifecycleAdapter,
} from '@confluence/malleable-ui-common/entry-points/ObjectSidebarRemixLifecycle.ts';
import {
	RegisteredExperience,
	type RovoConfigProviderBaseProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { RovoConfigProvider } from '@confluence/rovo-config-provider-common/entry-points/RovoConfigProvider.ts';
import { FolderRovoConfigProvider } from '@confluence/rovo-config-provider-common/entry-points/FolderRovoConfigProvider.ts';
import { ViewPageRovoConfigProvider } from '@confluence/view-page-rovo-config-provider/entry-points/ViewPageRovoConfigProvider.ts';
import type { PageRovoContributorsRef } from '@confluence/view-page-rovo-config-provider/entry-points/PageRovoContributors.ts';
import type { RovoActivationFragmentRef } from '@confluence/page-rovo-config-common/entry-points/RovoActivation.ts';
import { EditPageRovoConfigProvider } from '@confluence/edit-page-rovo-config-provider/entry-points/EditPageRovoConfigProvider.ts';
import { useReadTime } from '@confluence/read-time/entry-points/useReadTime.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import type { AgentTrustReceipt } from '@confluence/agent-trust-receipt/entry-points/agentTrustReceiptModel.ts';
import type { FloatingTableOfContentsPlugin } from '@confluence/floating-table-of-contents-common/entry-points/floatingTableOfContentsPlugin.ts';

import { ObjectSidebarRemixLifecycleSessionProvider } from './remix/ObjectSidebarRemixLifecycleSession.tsx';
import { ReviewButtonLoader } from './sidebar-buttons/ReviewButtonLoader.tsx';
import { TableOfContentsPopupButtonLoader } from './sidebar-buttons/TableOfContentsPopupButtonLoader.tsx';
import { AudioButtonLoaderWrapper } from './sidebar-buttons/AudioButtonLoaderWrapper.tsx';
import { EnterpriseHerButtonWrapper } from './sidebar-buttons/EnterpriseHerButtonWrapper.tsx';
import { OBJECT_SIDEBAR_TOUR_TARGETS } from './objectSidebarTourTargets.ts';
import { OBJECT_SIDEBAR_CONTROL_METRIC } from './perf.config.ts';

const hoistedNav4RightSlotWidthVar = ['--right', 'PanelWidth'].join('');

const CommentPanelButton = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-CommentPanelButton" */ './sidebar-buttons/CommentPanelButton.tsx'
			)
		).CommentPanelButton,
});

const DetailsPanelButton = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-DetailsPanelButton" */ './sidebar-buttons/DetailsPanelButton.tsx'
			)
		).DetailsPanelButton,
});

const AgentReceiptPanelButton = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AgentReceiptPanelButton" */ './sidebar-buttons/AgentReceiptPanelButton.tsx'
			)
		).AgentReceiptPanelButton,
});

const EditorAIActions = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-EditorAIActions" */ './sidebar-buttons/EditorAIActions.tsx'
			)
		).EditorAIActions,
});

const UndoButton = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-UndoRedoButton" */ './sidebar-buttons/UndoRedoButton.tsx'
			)
		).UndoButton,
});

const RedoButton = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-UndoRedoButton" */ './sidebar-buttons/UndoRedoButton.tsx'
			)
		).RedoButton,
});

const ShowDiffButton = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ShowDiffButton" */ './sidebar-buttons/ShowDiffButton.tsx'
			)
		).ShowDiffButton,
});

const RemixPanelButton = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-RemixPanelButton" */ './remix/RemixPanelButton.tsx'
			)
		).RemixPanelButton,
});

const AgentsPanelButton = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AgentsPanelButton" */ './sidebar-buttons/AgentsPanelButton.tsx'
			)
		).AgentsPanelButton,
});

const AudioListenButtonWrapper = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-AudioListenButtonWrapper" */ './sidebar-buttons/AudioListenButtonWrapper.tsx'
			)
		).AudioListenButtonWrapper,
});

const ObjectSidebarSpotlightTour = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-ObjectSidebarSpotlightTour" */ './ObjectSidebarSpotlightTour.tsx'
			)
		).ObjectSidebarSpotlightTour,
});

const objectSidebarStyles = css({
	position: 'fixed',
	bottom: 0,
	zIndex: layers.dialog(),
	transition:
		'right 300ms cubic-bezier(0.2, 0, 0, 1) 0s, margin-right 300ms cubic-bezier(0.2, 0, 0, 1) 0s',
	pointerEvents: 'none',
	// The selector below ensures the elements behind the empty space taken up by the container remain clickable/responsive to mouse events
	// eslint-disable-next-line @atlaskit/design-system/no-nested-styles, @atlaskit/ui-styling-standard/no-nested-selectors
	'& > *': {
		pointerEvents: 'auto',
	},
});

const markdownModeObjectSidebarStyles = css({
	bottom: token('space.500'),
});

const objectSidebarPaddingStyles = css({
	paddingRight: `${token('space.300')}`,
	paddingBottom: `${token('space.100')}`,
});

const databaseObjectSidebarPaddingStyles = css({
	paddingRight: `${token('space.100')}`,
	paddingBottom: `${token('space.100')}`,
});

const largeBottomPaddingStyles = css({
	paddingBottom: `${token('space.300')}`,
});

const viewModeStyles = css({
	//z index is set to 2 to ensure that the object sidebar is below the inline comment component but above the "Add a comment" button
	zIndex: 2,
});

const miniModalModeStyles = css({
	//z index is set to modal to ensure that the mini modal renders above everything else as expected
	zIndex: layers.modal(),
});

const collapsibleObjectSidebarContainerStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: 'radius.xlarge',
});

const collapsibleObjectSidebarContainerOverlayStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.overlay',
	backgroundColor: 'elevation.surface.overlay',
	borderRadius: 'radius.xlarge',
});

const collapsibleSquircleObjectSidebarContainerStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.raised',
	backgroundColor: 'elevation.surface.raised',
	borderRadius: 'radius.xlarge',
});

const collapsibleSquircleObjectSidebarContainerOverlayStyles = xcss({
	padding: 'space.100',
	boxShadow: 'elevation.shadow.overlay',
	backgroundColor: 'elevation.surface.overlay',
	borderRadius: 'radius.xlarge',
});

const collapsibleContainerStyles = css({
	width: `${token('space.600')}`,
	marginLeft: 'auto',
});

const collapsibleBottomSidebarStyles = xcss({
	paddingTop: 'space.100',
	marginBottom: 'space.050',
});

const rovoButtonCollapsibleBottomSidebarStyles = css({
	paddingTop: token('space.100'),
	marginBottom: token('space.200'),
	// eslint-disable-next-line @atlaskit/design-system/use-tokens-space
	marginLeft: '1px',
});

const collapsibleToggleStyles = css({
	margin: 'auto',
	width: `${token('space.400')}`,
});

const dividerStyle = xcss({
	borderBottom: `${token('border.width.selected')} solid ${token('color.border')}`,
	display: 'inline-block',
	width: '16px',
	height: '1px',
});

const hiddenStyles = css({
	display: 'none',
});

const i18n = defineMessages({
	expandToggleLabel: {
		id: 'object-sidebar-components.expand-toggle-label',
		defaultMessage: 'Expand action bar',
		description: 'Label for button to expand the floating action bar',
	},
	collapseToggleLabel: {
		id: 'object-sidebar-components.collapse-toggle-label',
		defaultMessage: 'Collapse action bar',
		description: 'Label for button to collapse the floating action bar',
	},
});

export type ObjectSidebarControlProps = {
	hasCommentsButton?: boolean;
	hasDetailsButton?: boolean;
	hasAgentReceiptButton?: boolean;
	hasLoomButton?: boolean;
	hasAiButton?: boolean;
	hasRemixButton?: boolean;
	/** Eligibility from editor configuration, available before the plugin API initializes. */
	hasReviewButton?: boolean;
	hasUndoRedoButton?: boolean;
	editorAIActions?: ObjectSidebarEditorAIAction[];
	undoRedoButtonActions?: {
		canUndo?: boolean;
		canRedo?: boolean;
		undo?: () => boolean;
		redo?: () => boolean;
	};
	showDiffButtonActions?: {
		isShowDiffSelected: boolean;
		isShowDiffEnabled: boolean;
		onShowDiff: () => void;
	};
	aiSuggestionsButtonActions?: {
		onStartThinking: () => void;
		fireAnalyticsEvent?: EditorAnalyticsAPI['fireAnalyticsEvent'];
		isThinking: boolean;
		showNotificationDot?: boolean;
	};
	hasAudioButton?: boolean;
	agentTrustReceipt?: AgentTrustReceipt | null;
	contentId: string;
	spaceKey?: string;
	contentType?: string | null;
	contentTitle?: string | null;
	lastModifiedDate?: string | null;
	pageCommentsSectionInView?: boolean;
	contentIdFromQuery?: string | null;
	contributorsFragmentRef?: PageRovoContributorsRef & PageHeaderLoomButton_contentContributors$key;
	rovoActivationFragmentRef?: RovoActivationFragmentRef;
	contentBodyDynamic?: { representation?: string | null; value?: string | null } | null;
	getAdfDocument?: () => Promise<import('@atlaskit/adf-schema').DocNode | null>;
	editorApi?: PublicPluginAPI<
		[UserPreferencesPlugin, FloatingTableOfContentsPlugin, OptionalPlugin<AIPlugin>]
	>;
	scrollToEditorHeading?: (headingId: string) => void;
	aiIsStreaming?: boolean;
	isUnpublishedDraft?: boolean;
	largeBottomPadding?: boolean;
	shouldDisableUndoRedo?: boolean;
	isDatabasesStagingArea?: boolean;
	disabled?: boolean;
	isEditorEmpty?: boolean;
	isMarkdownMode?: boolean;
	isMarkdownModeSyntaxViewToggled?: boolean;
	actionsToRender?: ConversationAction[];
	preloadedCanAddComments?: boolean;
};

const ObjectSidebarControlContent: React.MemoExoticComponent<
	({
		hasCommentsButton,
		hasDetailsButton,
		hasLoomButton,
		hasAiButton,
		hasRemixButton,
		hasReviewButton,
		undoRedoButtonActions,
		hasUndoRedoButton,
		showDiffButtonActions,
		hasAudioButton,
		contentId,
		contributorsFragmentRef,
		rovoActivationFragmentRef,
		spaceKey,
		contentType,
		contentTitle,
		lastModifiedDate,
		aiIsStreaming,
		pageCommentsSectionInView,
		contentBodyDynamic,
		getAdfDocument,
		editorApi,
		scrollToEditorHeading,
		isUnpublishedDraft,
		largeBottomPadding,
		editorAIActions,
		shouldDisableUndoRedo,
		isDatabasesStagingArea,
		disabled,
		isEditorEmpty,
		actionsToRender,
		preloadedCanAddComments,
	}: ObjectSidebarControlProps) => React.JSX.Element | null
> = memo(
	({
		hasCommentsButton,
		hasDetailsButton,
		hasAgentReceiptButton,
		hasLoomButton,
		hasAiButton,
		hasRemixButton,
		hasReviewButton,
		undoRedoButtonActions,
		hasUndoRedoButton,
		showDiffButtonActions,
		aiSuggestionsButtonActions,
		hasAudioButton,
		agentTrustReceipt,
		contentId,
		contributorsFragmentRef,
		rovoActivationFragmentRef,
		spaceKey,
		contentType,
		contentTitle,
		lastModifiedDate,
		aiIsStreaming = false,
		pageCommentsSectionInView,
		contentBodyDynamic,
		editorApi,
		scrollToEditorHeading,
		getAdfDocument,
		isUnpublishedDraft,
		largeBottomPadding,
		editorAIActions,
		shouldDisableUndoRedo = false,
		isDatabasesStagingArea = false,
		disabled,
		isEditorEmpty,
		isMarkdownMode,
		isMarkdownModeSyntaxViewToggled,
		actionsToRender,
		preloadedCanAddComments,
	}: ObjectSidebarControlProps): React.JSX.Element | null => {
		const [{ isCollabOffline }] = useCollabNetworkStatus();

		// Network status is used when:
		// - isCollabOffline: The collab provider has detected a network disconnection
		// - platform_editor_offline_editing_wrapper_override: Kill switch to disable this feature (must be disabled)
		const isNetworkDisabled =
			isCollabOffline && !fg('platform_editor_offline_editing_wrapper_override');
		const { isFullScreenEmbedView } = useReactProps_DO_NOT_USE<IframePassThroughProps>() || {};

		const { formatMessage } = useIntl();
		const [actionBarIsHovered, setActionBarIsHovered] = useState(false);
		const objectSidebarControlRef = useRef<HTMLDivElement>(null);
		const sidebarControlState = useSidebarControlState();
		const sidebarControlExpandCollapsedState = useSidebarControlExpandCollapsedState();
		const isObjectSidebarShown = useIsObjectSidebarShown();
		const {
			setSidebarControlState,
			showSidebarControlIfNoPanel,
			toggleObjectSidebarControl,
			setSidebarControlExpandCollapsedState,
		} = useObjectSidebarActions();

		const pageMode = useGetPageMode();
		const { isAdminHubAIEnabled } = useSessionData();

		const isLoomDisabled = useIsLoomDisabled();

		const { mode: rovoExperienceMode } = useRovoExperienceAPIStore();
		const isRovoButtonEnabled = useAIRovoButtonEnablement();
		const { isMobileApp } = useIsMobileApp();

		const isOnPDFExportPageRouteArgs = {
			selector: (routeName: string | undefined) => {
				if (!routeName) {
					return false;
				}
				return routeName === PDF_EXPORT_PAGE.name;
			},
		};

		const isOnPDFExportPageRoute = useRouteName(isOnPDFExportPageRouteArgs);

		const isLivePage = useIsCurrentPageLive();
		const [{ isViewMode: isLiveViewMode }] = useLivePageMode();
		const skipReadTime =
			!isAdminHubAIEnabled ||
			!isRovoButtonEnabled ||
			pageMode !== PageMode.LIVE ||
			isLiveViewMode ||
			!fg('confluence_rovo_live_doc_nudges');
		const { readTime, loading: readTimeLoading } = useReadTime(contentId, skipReadTime);
		const renderServerPlaceholder = useIsSSROrHydrating();

		const { isAudioV3Enabled } = useAudioFeatureFlags();
		// Rovo streams a handed-off Remix into the editor and disables its AI entry points, but a stop or
		// panel close never reaches the editor, so they would stay disabled until the stream times out.
		const releaseEditorAiEntryPoints = useCallback(() => {
			editorApi?.aiExperience?.actions.toggleAiEntryPoints?.(false);
		}, [editorApi]);
		const objectSidebarRemixLifecycleAdapter = useObjectSidebarRemixLifecycleAdapter({
			onHandedOffGenerationEnded: releaseEditorAiEntryPoints,
		});

		// whenever the contentID changes we needed to reset the action bar to be expanded
		useEffect(() => {
			setSidebarControlExpandCollapsedState('expanded');
		}, [contentId, setSidebarControlExpandCollapsedState]);

		useEffect(() => {
			if (expValEquals('cc_comments_detached_in_open_view', 'isEnabled', true)) {
				showSidebarControlIfNoPanel();
			} else if (!isObjectSidebarShown) {
				setSidebarControlState('shown');
			}
			setSidebarControlExpandCollapsedState('expanded');
			return () => {
				setSidebarControlExpandCollapsedState('collapsed');
			};
		}, [
			showSidebarControlIfNoPanel,
			setSidebarControlState,
			setSidebarControlExpandCollapsedState,
			isObjectSidebarShown,
		]);

		const sidebarIsExpanded =
			sidebarControlExpandCollapsedState === 'expanded' || renderServerPlaceholder;

		// Collapsed actions are rendered when collapsed. These can be used e.g. to enable keyboard shortcuts while collapsed.
		const collapsedActions = [];
		const primaryActions: JSX.Element[] = [];

		if (hasUndoRedoButton && undoRedoButtonActions && !isLiveViewMode && isDatabasesStagingArea) {
			const { canUndo, undo, canRedo, redo } = undoRedoButtonActions;

			if (undo && canUndo !== undefined) {
				primaryActions.push(
					<UndoButton
						canUndo={canUndo}
						undo={undo}
						isDisabled={aiIsStreaming || shouldDisableUndoRedo}
					/>,
				);
			}

			if (redo && canRedo !== undefined) {
				primaryActions.push(
					<RedoButton
						canRedo={canRedo}
						redo={redo}
						isDisabled={aiIsStreaming || shouldDisableUndoRedo}
					/>,
				);
			}
		}
		if (showDiffButtonActions && !isLiveViewMode && isDatabasesStagingArea) {
			const { onShowDiff, isShowDiffEnabled, isShowDiffSelected } = showDiffButtonActions;

			primaryActions.push(
				<ShowDiffButton
					isShowDiffSelected={isShowDiffSelected}
					isShowDiffEnabled={isShowDiffEnabled && !aiIsStreaming}
					onShowDiff={onShowDiff}
				/>,
			);
		}

		const isPageOrBlogpost = contentType === 'page' || contentType === 'blogpost';

		const commentsButton = hasCommentsButton ? (
			<CommentPanelButton
				key="comment-panel-button"
				contentId={contentId}
				isDisabled={isNetworkDisabled}
				preloadedCanAddComments={preloadedCanAddComments}
			/>
		) : null;
		const collapsedCommentsButton =
			hasCommentsButton && !sidebarIsExpanded ? (
				<CommentPanelButton
					key="comment-panel-button-collapsed"
					contentId={contentId}
					collapsed
					isDisabled={isNetworkDisabled}
					preloadedCanAddComments={preloadedCanAddComments}
				/>
			) : null;

		const detailsButton = hasDetailsButton ? (
			<DetailsPanelButton
				key="details-panel-button"
				contentId={contentId}
				isDisabled={isNetworkDisabled}
			/>
		) : null;
		const collapsedDetailsButton =
			hasDetailsButton && !sidebarIsExpanded ? (
				<DetailsPanelButton
					key="details-panel-button-collapsed"
					contentId={contentId}
					collapsed
					isDisabled={isNetworkDisabled}
				/>
			) : null;

		const agentReceiptButton =
			hasAgentReceiptButton && agentTrustReceipt ? (
				<AgentReceiptPanelButton
					key="agent-receipt-panel-button"
					receipt={agentTrustReceipt}
					isDisabled={isNetworkDisabled}
				/>
			) : null;

		const enterpriseHerButton = isExperimentEnabled('enterprise_her') ? (
			<EnterpriseHerButtonWrapper
				key="enterprise-her-button-wrapper"
				contentId={contentId}
				contentType={contentType ?? ''}
			/>
		) : null;

		let audioButton: JSX.Element | null = null;
		if (hasAudioButton) {
			audioButton = isAudioV3Enabled ? (
				<AudioListenButtonWrapper
					key="audio-listen-button-wrapper"
					isRovoButtonEnabled={isRovoButtonEnabled}
					contentId={contentId}
					contentType={contentType ?? ''}
					isLivePage={isLivePage}
					spaceKey={spaceKey ?? ''}
					title={contentTitle ?? ''}
					isAutoOpenDisabled={disabled}
				/>
			) : (
				<AudioButtonLoaderWrapper
					key="audio-button-loader-wrapper"
					contentId={contentId}
					contentType={contentType}
					spaceKey={spaceKey}
					contentTitle={contentTitle}
					isDisabled={isNetworkDisabled}
					isView={pageMode === PageMode.VIEW}
				/>
			);
		}

		// AI Suggested Edits ships a reorganised toolbar without Loom on pages and blog posts.
		// Read without exposure because the toolbar also renders on view pages, where the Review
		// entry point (which logs exposure) never does.
		const loomButton =
			hasLoomButton &&
			!isLoomDisabled &&
			!(
				isPageOrBlogpost &&
				UNSAFE_expValNoExposure('platform_editor_ai_content_suggested_edits', 'isEnabled', false)
			) ? (
				<PageHeaderLoomEntryPoint
					contributorsFragmentRef={contributorsFragmentRef}
					key="page-header-loom-entry-point"
					contentId={contentId}
					crossSellPopupPlacement="left-end"
					crossSellSpotlightPlacement="left-start" // "left-start" in spotlight component (https://atlassian.design/components/spotlight/examples#placements) is equivalent to "left-end" in popup component (https://atlassian.design/components/popup/examples#placement)
					spaceKey={spaceKey ?? ''}
					contentType={contentType ?? ''}
					isCircle={!isRovoButtonEnabled}
					testId="sidebar-loom-button"
					source="contentWrapper"
					isDisabled={isNetworkDisabled}
				/>
			) : null;

		const editorAIActionsButton = editorAIActions ? (
			<EditorAIActions
				key="editor-ai-actions"
				editorAIActions={editorAIActions}
				isDisabled={aiIsStreaming}
			/>
		) : null;

		// Computed once: the Remix entry point gates both this button and the persistently hosted
		// Rovo submit coordinator, and `expValEquals` records an exposure on every call.
		const isRemixEntryEligible = Boolean(
			hasRemixButton &&
			!isRemixOptedOut() &&
			expValEquals('cc-maui-object-level-remix-experiment', 'isEnabled', true),
		);
		const remixButton = isRemixEntryEligible ? (
			<RemixPanelButton
				key="remix-panel-button"
				contentBodyValue={contentBodyDynamic?.value}
				getAdfDocument={getAdfDocument}
				isEditorEmpty={isEditorEmpty}
				isDisabled={isNetworkDisabled}
			/>
		) : null;

		const shouldRenderFloatingTableOfContents =
			(contentType === 'page' || contentType === 'blogpost') &&
			(pageMode === PageMode.VIEW ||
				editorApi ||
				((pageMode === PageMode.EDIT || pageMode === PageMode.LIVE) &&
					fg('confluence_sidebar_buttons_initial_render'))) &&
			expValEquals('platform_editor_floating_toc', 'isEnabled', true);

		const tableOfContentsButton = shouldRenderFloatingTableOfContents ? (
			<TableOfContentsPopupButtonLoader
				key="table-of-contents-popup-button"
				isDisabled={
					pageMode !== PageMode.VIEW &&
					!editorApi &&
					fg('confluence_sidebar_buttons_initial_render')
				}
				contentBodyValue={contentBodyDynamic?.value}
				contentType={contentType}
				editorApi={editorApi}
				pageMode={pageMode}
				popupPositioningRef={objectSidebarControlRef}
				scrollToEditorHeading={scrollToEditorHeading}
			/>
		) : null;

		const isEditingPage =
			pageMode === PageMode.EDIT || (pageMode === PageMode.LIVE && !isLiveViewMode);

		const reviewButton =
			(aiSuggestionsButtonActions ||
				(hasReviewButton && fg('confluence_sidebar_buttons_initial_render'))) &&
			isEditingPage &&
			expValEquals('platform_editor_ai_content_suggested_edits', 'isEnabled', true) ? (
				<ReviewButtonLoader
					key="review-button"
					onStartThinking={aiSuggestionsButtonActions?.onStartThinking}
					fireAnalyticsEvent={aiSuggestionsButtonActions?.fireAnalyticsEvent}
					isDisabled={!aiSuggestionsButtonActions || isNetworkDisabled}
					isVisible={renderServerPlaceholder || sidebarControlState !== 'hidden'}
					showNotificationDot={aiSuggestionsButtonActions?.showNotificationDot}
				/>
			) : null;

		const agentsButton = fg('confluence_ai_agents_sidebar_button') ? (
			<AgentsPanelButton
				key="agents-panel-button"
				contentId={contentId}
				isDisabled={isNetworkDisabled}
			/>
		) : null;

		const orderedSecondaryActions =
			isPageOrBlogpost &&
			UNSAFE_expValNoExposure('platform_editor_ai_content_suggested_edits', 'isEnabled', false)
				? [
						// Consumer actions
						tableOfContentsButton,
						detailsButton,
						agentReceiptButton,
						enterpriseHerButton,
						audioButton,
						// Collaboration actions
						commentsButton,
						reviewButton,
						// Creator actions
						editorAIActionsButton,
						remixButton,
						agentsButton,
					]
				: [
						tableOfContentsButton,
						commentsButton,
						detailsButton,
						agentReceiptButton,
						enterpriseHerButton,
						audioButton,
						loomButton,
						editorAIActionsButton,
						remixButton,
						reviewButton,
						agentsButton,
					];
		const secondaryActionsTeam25: JSX.Element[] = [];
		for (const action of orderedSecondaryActions) {
			if (action) {
				secondaryActionsTeam25.push(action);
			}
		}

		const orderedCollapsedActions =
			isPageOrBlogpost &&
			UNSAFE_expValNoExposure('platform_editor_ai_content_suggested_edits', 'isEnabled', false)
				? [collapsedDetailsButton, collapsedCommentsButton]
				: [collapsedCommentsButton, collapsedDetailsButton];
		for (const action of orderedCollapsedActions) {
			if (action) {
				collapsedActions.push(action);
			}
		}

		const rovoConfiguration: RovoConfigProviderBaseProps | undefined = useMemo(() => {
			if (contentType === 'folder') {
				return {
					experience: RegisteredExperience.FOLDER,
					provider: FolderRovoConfigProvider,
				};
			}

			if (pageMode === PageMode.VIEW || (pageMode === PageMode.LIVE && isLiveViewMode)) {
				return {
					experience: RegisteredExperience.VIEW_PAGE,
					provider: ViewPageRovoConfigProvider,
				};
			}

			if (pageMode === PageMode.EDIT || (pageMode === PageMode.LIVE && !isLiveViewMode)) {
				return {
					experience: RegisteredExperience.EDIT_PAGE,
					provider: EditPageRovoConfigProvider,
				};
			}

			return undefined;
		}, [contentType, pageMode, isLiveViewMode]);

		if (isOnPDFExportPageRoute) {
			return null;
		}

		const isEmbeddedConfluence = isEmbeddedConfluence_DO_NOT_USE();
		// For databases in staging area, always allow rendering (isFullScreenEmbedView is typically undefined)
		const shouldRenderWhenEmbedded = isEmbeddedConfluence
			? isDatabasesStagingArea || isFullScreenEmbedView
			: !isEmbeddedConfluence;

		// If there's no AI button, when we collapse the action bar, we need to render an expand icon within the
		// existing action bar container. If there is an AI button, hovering over it should render the expand icon
		const shouldRenderIntegratedExpandButton = !hasAiButton && !sidebarIsExpanded;
		// We always render the toggle component into the DOM, but we change its visibility based on:
		// If the action bar is expanded: show on hover (regardless of AI button)
		// If the action bar is collapsed: always show if AI button present, otherwise never show
		const shouldDisplayHoverToggle =
			(sidebarIsExpanded && actionBarIsHovered) || (hasAiButton && !sidebarIsExpanded);

		const shouldRenderAISmartButton = hasAiButton && shouldRenderWhenEmbedded && !isMobileApp;
		const isRovoMiniModalShown =
			shouldRenderAISmartButton &&
			isRovoButtonEnabled &&
			rovoExperienceMode === ConversationAssistantUIModes.MINI_MODAL;

		const handleMouseEnter = () => {
			setActionBarIsHovered(true);
		};

		const handleMouseLeave = () => {
			setActionBarIsHovered(false);
		};

		const onToggleClick = () => {
			toggleObjectSidebarControl();
			// need to make sure the hover (dis)appears after clicking it
			setActionBarIsHovered(!sidebarIsExpanded);
		};

		const toggleLabel = sidebarIsExpanded
			? formatMessage(i18n.collapseToggleLabel)
			: formatMessage(i18n.expandToggleLabel);

		const integratedExpandButton = (
			<Tooltip position="left" content={formatMessage(i18n.expandToggleLabel)} hideTooltipOnClick>
				<IconButton
					appearance="subtle"
					icon={(iconProps: any) => <ChevronUpIcon {...iconProps} size="small" />}
					label={toggleLabel}
					onClick={toggleObjectSidebarControl}
					shape="circle"
					testId="integrated-expand-button"
				/>
			</Tooltip>
		);

		return (
			<>
				<div
					css={[
						objectSidebarStyles,
						isMarkdownMode && isMarkdownModeSyntaxViewToggled && markdownModeObjectSidebarStyles,
						(contentType === 'page' || contentType === 'blogpost' || pageMode === PageMode.VIEW) &&
							objectSidebarPaddingStyles,
						(contentType === 'database' || contentType === 'slide') &&
							databaseObjectSidebarPaddingStyles,
						largeBottomPadding && largeBottomPaddingStyles,
						isRovoMiniModalShown && miniModalModeStyles,
						pageMode === PageMode.VIEW && !isRovoMiniModalShown && viewModeStyles,
						!renderServerPlaceholder && sidebarControlState === 'hidden' && hiddenStyles,
					]}
					{...(UNSAFE_expValNoExposure('cc_maui_remix_button_hover_corridor', 'isEnabled', false)
						? blockControlsSuppressionProps
						: {})}
					data-testid="object-sidebar-container"
					data-vc="object-sidebar-container"
					style={{
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						right:
							pageMode === PageMode.VIEW
								? `calc(var(${hoistedNav4RightSlotWidthVar}, 0px))`
								: '0px',
						// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop
						marginRight: 0,
					}}
					onMouseEnter={handleMouseEnter}
					onMouseLeave={handleMouseLeave}
					onFocus={handleMouseEnter}
					onBlur={handleMouseLeave}
				>
					<ObjectSidebarRemixLifecycleSessionProvider
						lifecycleAdapter={objectSidebarRemixLifecycleAdapter}
						contentBodyValue={contentBodyDynamic?.value}
						getAdfDocument={getAdfDocument}
						isRemixEntryEligible={isRemixEntryEligible}
						isObjectSidebarExpanded={sidebarIsExpanded}
					>
						{primaryActions.length + secondaryActionsTeam25.length > 0 &&
							shouldRenderWhenEmbedded &&
							!isMobileApp && (
								<SpotlightManager>
									{fg('confluence_frontend_spotlight_ads_migration') ? (
										<PopoverProvider>
											<PopoverTarget>
												<div css={collapsibleContainerStyles}>
													<Tooltip position="left" content={toggleLabel} hideTooltipOnClick>
														<div
															css={collapsibleToggleStyles}
															style={{
																paddingBottom: sidebarIsExpanded
																	? `${token('space.100')}`
																	: undefined,
																visibility: shouldDisplayHoverToggle ? 'visible' : 'hidden',
															}}
															data-testid="collapsible-hover-toggle"
														>
															<IconButton
																appearance="subtle"
																icon={(iconProps: any) =>
																	sidebarIsExpanded ? (
																		<ChevronDownIcon {...iconProps} size="small" />
																	) : (
																		<ChevronUpIcon {...iconProps} size="small" />
																	)
																}
																label={toggleLabel}
																onClick={onToggleClick}
																shape="circle"
																testId="collapsible-hover-button"
															/>
														</div>
													</Tooltip>
												</div>
											</PopoverTarget>
										</PopoverProvider>
									) : (
										<SpotlightTarget name={OBJECT_SIDEBAR_TOUR_TARGETS.OBJECT_SIDEBAR_CONTROL}>
											<div css={collapsibleContainerStyles}>
												<Tooltip position="left" content={toggleLabel} hideTooltipOnClick>
													<div
														css={collapsibleToggleStyles}
														style={{
															paddingBottom: sidebarIsExpanded
																? `${token('space.100')}`
																: undefined,
															visibility: shouldDisplayHoverToggle ? 'visible' : 'hidden',
														}}
														data-testid="collapsible-hover-toggle"
													>
														<IconButton
															appearance="subtle"
															icon={(iconProps: any) =>
																sidebarIsExpanded ? (
																	<ChevronDownIcon {...iconProps} size="small" />
																) : (
																	<ChevronUpIcon {...iconProps} size="small" />
																)
															}
															label={toggleLabel}
															onClick={onToggleClick}
															shape="circle"
															testId="collapsible-hover-button"
														/>
													</div>
												</Tooltip>
											</div>
										</SpotlightTarget>
									)}
									{fg('confluence_frontend_spotlight_ads_migration') ? (
										<PopoverTarget>
											<div css={collapsibleContainerStyles}>
												{sidebarIsExpanded || renderServerPlaceholder ? (
													<Flex
														ref={
															shouldRenderFloatingTableOfContents
																? objectSidebarControlRef
																: undefined
														}
														xcss={
															isRovoButtonEnabled
																? expValEquals('cwr_blank_object_experiment', 'isEnabled', true)
																	? collapsibleSquircleObjectSidebarContainerOverlayStyles
																	: collapsibleSquircleObjectSidebarContainerStyles
																: expValEquals('cwr_blank_object_experiment', 'isEnabled', true)
																	? collapsibleObjectSidebarContainerOverlayStyles
																	: collapsibleObjectSidebarContainerStyles
														}
														direction="column"
														gap="space.100"
														justifyContent="center"
														alignItems="center"
														testId="object-sidebar-control"
													>
														{primaryActions}
														{primaryActions.length > 0 && secondaryActionsTeam25.length > 0 && (
															<Box xcss={dividerStyle} />
														)}
														{secondaryActionsTeam25}
													</Flex>
												) : (
													<>{collapsedActions}</>
												)}
											</div>
										</PopoverTarget>
									) : (
										<div css={collapsibleContainerStyles}>
											{sidebarIsExpanded || renderServerPlaceholder ? (
												<Flex
													ref={
														shouldRenderFloatingTableOfContents
															? objectSidebarControlRef
															: undefined
													}
													xcss={
														isRovoButtonEnabled
															? expValEquals('cwr_blank_object_experiment', 'isEnabled', true)
																? collapsibleSquircleObjectSidebarContainerOverlayStyles
																: collapsibleSquircleObjectSidebarContainerStyles
															: expValEquals('cwr_blank_object_experiment', 'isEnabled', true)
																? collapsibleObjectSidebarContainerOverlayStyles
																: collapsibleObjectSidebarContainerStyles
													}
													direction="column"
													gap="space.100"
													justifyContent="center"
													alignItems="center"
													testId="object-sidebar-control"
												>
													{primaryActions}
													{primaryActions.length > 0 && secondaryActionsTeam25.length > 0 && (
														<Box xcss={dividerStyle} />
													)}
													{secondaryActionsTeam25}
												</Flex>
											) : (
												<>{collapsedActions}</>
											)}
										</div>
									)}
									{shouldRenderIntegratedExpandButton ? (
										<div css={collapsibleContainerStyles}>
											<Flex
												xcss={
													isRovoButtonEnabled
														? expValEquals('cwr_blank_object_experiment', 'isEnabled', true)
															? collapsibleSquircleObjectSidebarContainerOverlayStyles
															: collapsibleSquircleObjectSidebarContainerStyles
														: expValEquals('cwr_blank_object_experiment', 'isEnabled', true)
															? collapsibleObjectSidebarContainerOverlayStyles
															: collapsibleObjectSidebarContainerStyles
												}
												direction="column"
												gap="space.100"
												justifyContent="center"
												alignItems="center"
												testId="object-sidebar-control"
											>
												{integratedExpandButton}
											</Flex>
										</div>
									) : null}
									{fg('confluence_frontend_spotlight_ads_migration') ? (
										<ObjectSidebarSpotlightTour contentId={contentId} />
									) : (
										<SpotlightTransition>
											<ObjectSidebarSpotlightTour contentId={contentId} />
										</SpotlightTransition>
									)}
								</SpotlightManager>
							)}
						{shouldRenderAISmartButton &&
							(isRovoButtonEnabled ? (
								<div css={rovoButtonCollapsibleBottomSidebarStyles}>
									<RovoConfigProvider
										experience={rovoConfiguration?.experience}
										provider={rovoConfiguration?.provider}
										contributorsFragmentRef={contributorsFragmentRef}
										rovoActivationFragmentRef={rovoActivationFragmentRef}
										contentId={contentId}
										contentType={contentType ?? ''}
										spaceKey={spaceKey ?? ''}
										contentTitle={contentTitle ?? ''}
										lastModifiedDate={lastModifiedDate}
										isUnpublishedDraft={isUnpublishedDraft}
										isEditorEmpty={isEditorEmpty}
										readTime={readTime ?? undefined}
										readTimeLoading={readTimeLoading}
										actionsToRender={actionsToRender ?? []}
									/>
									<AISmartButtonWrapper
										contentId={contentId}
										contentType={contentType ?? ''}
										pageMode={pageMode}
										isDisabled={isNetworkDisabled}
										spaceKey={spaceKey}
										isSidebarAvailable={isEmbeddedConfluence ? false : undefined}
									/>
								</div>
							) : (
								contentType !== 'database' && (
									<Box xcss={collapsibleBottomSidebarStyles}>
										<AIFloatingContextMenu
											spaceKey={spaceKey ?? ''}
											contentId={contentId}
											contentType={contentType ?? ''}
											lastModifiedDate={lastModifiedDate}
											pageCommentsSectionInView={pageCommentsSectionInView}
											isLivePage={isLivePage}
											isInObjectSideBar
										/>
									</Box>
								)
							))}
					</ObjectSidebarRemixLifecycleSessionProvider>
				</div>
				<PageSegmentLoadEnd metric={OBJECT_SIDEBAR_CONTROL_METRIC} />
			</>
		);
	},
);

export const ObjectSidebarControl: React.MemoExoticComponent<
	(props: ObjectSidebarControlProps) => React.JSX.Element
> = memo(
	(props: ObjectSidebarControlProps): React.JSX.Element =>
		fg('remix_state_rearchitecture_enabled') ? (
			<ObjectSidebarRemixLifecycleOwnerProvider>
				<ObjectSidebarControlContent {...props} />
			</ObjectSidebarRemixLifecycleOwnerProvider>
		) : (
			<ObjectSidebarControlContent {...props} />
		),
);
